import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Gauge, Activity, Clock, ChevronRight, Sparkles, CalendarDays } from "lucide-react";
import Shell from "@/components/Shell";
import DriftRadar from "@/components/flow/DriftRadar";
import Brandmark from "@/components/Brandmark";
import InstallPrompt from "@/components/InstallPrompt";
import { http, MODE_TINT } from "@/lib/api";
import { useApp } from "@/lib/store";
import { track } from "@/lib/analytics";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

// Curiosity-based return prompts (never guilt, never streaks).
const RETURN_PROMPTS = [
  "Has your state changed since your last check-in?",
  "Before your next demand, check your state.",
  "How are you operating right now?",
];

const REC_LABEL = { balanced: "In balance", building: "Building", high: "Recovery is lagging" };

export default function Home() {
  const nav = useNavigate();
  const { deviceId, progress, refreshProgress, isPlus } = useApp();
  const [lr, setLr] = useState(null);
  const [last, setLast] = useState(undefined); // undefined = loading, null = none

  useEffect(() => {
    track("home_view");
    refreshProgress();
    http.get(`/load-recovery/${deviceId}`).then(({ data }) => setLr(data)).catch(() => setLr(null));
    http.get(`/history/${deviceId}`)
      .then(({ data }) => setLast((data.items && data.items[0]) || null))
      .catch(() => setLast(null));
  }, [deviceId, refreshProgress]);

  const pct = Math.min(100, Math.round(((progress?.count || 0) / (progress?.needed || 14)) * 100));
  const returning = (progress?.count || 0) > 0;
  const hasWeek = (progress?.count || 0) >= 4;
  const prompt = returning
    ? RETURN_PROMPTS[(new Date().getDate()) % RETURN_PROMPTS.length]
    : "How are you operating right now?";

  return (
    <Shell>
      <div className="px-6 pt-14 pb-28">
        {/* Greeting */}
        <Brandmark size={34} className="mb-1" />
        <h1 className="mt-3 font-serif-display text-4xl leading-tight" data-testid="home-greeting">
          {greeting()}.
        </h1>
        <p className="mt-2 text-[14.5px] leading-relaxed text-[#474747]">
          Know your state before the next demand — a meeting, a decision, a hard conversation, a shift.
        </p>
        {progress?.stage && (progress.count || 0) > 0 && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#F1EFE8] px-3 py-1" data-testid="home-stage">
            <span className="h-1.5 w-1.5 rounded-full bg-[#C9A227]" />
            <span className="text-[11.5px] font-medium text-[#474747]">{progress.stage.label}</span>
          </div>
        )}

        <InstallPrompt />

        {/* Primary check-in CTA */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="mt-6 ss-card p-6 border-l-2 border-[#C9A227]" data-testid="home-checkin-card">
            <p className="font-serif-display text-2xl leading-snug" data-testid="home-checkin-prompt">{prompt}</p>
            <p className="mt-2 text-[13.5px] text-[#6B6B6B]">
              Three quick questions. One realistic next move, in about a minute.
            </p>
            <button
              data-testid="home-start-checkin"
              onClick={() => nav("/navigate")}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 text-base font-medium ss-tap ss-focus hover:-translate-y-0.5">
              Start a check-in <ArrowRight size={18} strokeWidth={1.8} />
            </button>
            <button
              data-testid="home-baseline"
              onClick={() => nav("/navigate?baseline=1")}
              className="mt-2 w-full text-center text-[13.5px] text-[#474747] hover:text-[#1A1A1A] ss-focus transition-colors py-1.5">
              I feel steady — record a baseline
            </button>
          </div>
        </motion.div>

        {/* Drift Radar (auto-hides when no signal) */}
        <DriftRadar className="mt-4" />

        {/* Weekly reflection — curiosity, not a dashboard */}
        {hasWeek && (
          <button
            data-testid="home-reflection"
            onClick={() => nav("/reflection")}
            className="mt-4 w-full ss-card p-5 text-left ss-focus hover:-translate-y-0.5 transition-transform">
            <div className="flex items-center gap-2">
              <CalendarDays size={16} strokeWidth={1.8} className="text-[#C9A227]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8a6d10]">Your week in state</span>
              <ChevronRight size={17} className="ml-auto text-[#9a9a94]" />
            </div>
            <p className="mt-2.5 text-[13.5px] leading-relaxed text-[#474747]">
              One honest observation about what changed across your week.
            </p>
          </button>
        )}

        {/* Load & Recovery snapshot */}
        {lr && (
          <button
            data-testid="home-load-recovery"
            onClick={() => nav("/load-recovery")}
            className="mt-4 w-full ss-card p-5 text-left ss-focus hover:-translate-y-0.5 transition-transform">
            <div className="flex items-center gap-2">
              <Gauge size={16} strokeWidth={1.8} className="text-[#C9A227]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8a6d10]">Load &amp; Recovery</span>
              <ChevronRight size={17} className="ml-auto text-[#9a9a94]" />
            </div>
            {lr.available === false ? (
              <p className="mt-2.5 text-[13.5px] leading-relaxed text-[#474747]" data-testid="home-lr-locked">
                Unlocks after {lr.needed} check-ins — you have {lr.count}. Keep going.
              </p>
            ) : lr.locked ? (
              <p className="mt-2.5 text-[13.5px] leading-relaxed text-[#474747]">
                {lr.recovery_headline} · {lr.signature_headline}
              </p>
            ) : (
              <p className="mt-2.5 text-[14px] leading-relaxed text-[#1A1A1A]" data-testid="home-lr-full">
                Recovery: <span className="font-medium">{REC_LABEL[lr.recovery?.level] || lr.recovery?.level}</span>
                {lr.signature?.dominant && <> · Load leans <span className="font-medium">{lr.signature.dominant}</span></>}
              </p>
            )}
          </button>
        )}

        {/* Progress toward Pattern Review */}
        <div className="mt-4 ss-card p-5" data-testid="home-pattern">
          <div className="flex items-center gap-2">
            <Activity size={16} strokeWidth={1.8} className="text-[#C9A227]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8a6d10]">Pattern Review</span>
          </div>
          {progress?.unlocked ? (
            <>
              <p className="mt-2.5 text-[13.5px] leading-relaxed text-[#474747]">
                Your Pattern Review is ready — {isPlus ? "see what your last several check-ins reveal." : "unlock it with Plus to see what your check-ins reveal."}
              </p>
              <button
                data-testid="home-open-pattern"
                onClick={() => nav("/pattern")}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-5 py-3 text-[14px] font-medium ss-tap ss-focus">
                {isPlus ? "Open Pattern Review" : (<><Sparkles size={15} strokeWidth={1.8} /> Unlock with Plus</>)}
              </button>
            </>
          ) : (
            <>
              <p className="mt-2.5 text-[13.5px] text-[#474747]">
                {progress?.count || 0} of {progress?.needed || 14} check-ins toward your first review.
              </p>
              <div className="mt-3 h-2 rounded-full bg-[#EBE9E4] overflow-hidden">
                <div className="h-full rounded-full bg-[#C9A227] transition-all" style={{ width: `${pct}%` }} data-testid="home-pattern-bar" />
              </div>
            </>
          )}
        </div>

        {/* Plus nudge — only at the "we found something recurring" moment */}
        {!isPlus && progress?.unlocked && (
          <motion.button
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            data-testid="home-plus-nudge"
            onClick={() => nav("/plus")}
            className="mt-4 w-full ss-card p-5 text-left border-l-2 border-[#C9A227] ss-focus hover:-translate-y-0.5 transition-transform">
            <div className="flex items-center gap-2">
              <Sparkles size={16} strokeWidth={1.8} className="text-[#C9A227]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8a6d10]">Signal State ID Plus</span>
            </div>
            <p className="mt-2.5 text-[14px] leading-relaxed text-[#1A1A1A]">
              We've found something recurring across your {progress.count} check-ins. Plus opens your
              full Pattern Review — which states repeat, and what has actually helped.
            </p>
            <span className="mt-3 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-[#8a6d10]">
              See what we found <ArrowRight size={15} strokeWidth={1.8} />
            </span>
          </motion.button>
        )}

        {/* Last check-in peek */}
        {last && (
          <button
            data-testid="home-last-checkin"
            onClick={() => nav("/history")}
            className="mt-4 w-full ss-card p-5 text-left ss-focus hover:-translate-y-0.5 transition-transform">
            <div className="flex items-center gap-2">
              <Clock size={16} strokeWidth={1.8} className="text-[#6B6B6B]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6B6B6B]">Last check-in</span>
              <ChevronRight size={17} className="ml-auto text-[#9a9a94]" />
            </div>
            <div className="mt-2.5 flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: last.is_baseline ? "#C9A227" : (MODE_TINT[last.mode] || "#EBE9E4") }} />
              <span className="text-[15px] font-semibold">{last.is_baseline ? "Baseline" : last.mode_label}</span>
              <span className="text-[11px] text-[#6B6B6B] font-mono-ss">
                {new Date(last.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            </div>
            {last.protocol_title && <p className="mt-1 text-[13px] text-[#6B6B6B]">{last.protocol_title}</p>}
          </button>
        )}
      </div>
    </Shell>
  );
}
